import { useQuery } from "@apollo/client";
import Icon from "components/Icons/Icon";
import { EXPLORE_COUNTRIES, EXPLORE_DESTINATIONS } from "graphql/queries/explore";
import { t } from "i18n/index";
import { PlanGateModal, usePlanGate } from "./planGate";
import { discoverPath } from "i18n/urlLocale";
import { useMarketingLang } from "i18n/useMarketingLang";
import { Compass } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import trackEvent from "components/tracking/trackEvent";
import { getHomeTranslations } from "../marketing/i18n/pages/home";
import { localizedDestinationLabel, POPULAR_DESTINATIONS } from "../pages/Home";

export type HeroSearchTab = "plan" | "explore";

const TAB_ICON_SIZE = 18;

const PLAN_PROMPT_KEYS = [
	{ key: "home.searchCard.planPrompt1", fallback: "Plan me a lovely route through Thailand..." },
	{ key: "home.searchCard.planPrompt2", fallback: "2 week trip to Vietnam with beaches & temples..." },
	{ key: "home.searchCard.planPrompt3", fallback: "Family road trip through Italy, 10 days..." },
	{ key: "home.searchCard.planPrompt4", fallback: "Romantic weekend in Paris, mid-October..." },
	{ key: "home.searchCard.planPrompt5", fallback: "Backpacking Southeast Asia on a budget..." },
];

// Animated typing/deleting placeholder textarea + submit button — shared by the
// homepage's "Plan" tab and the entity-specific plan-only card (country/destination
// guides), which each supply their own prompt list, CTA label and submit handler.
function AnimatedPlanBox({ prompts, ctaLabel, onSubmit }: { prompts: string[]; ctaLabel: string; onSubmit?: () => void }) {
	const [promptIdx, setPromptIdx] = useState(0);
	const [displayed, setDisplayed] = useState("");
	const [isDeleting, setIsDeleting] = useState(false);

	useEffect(() => {
		const target = prompts[promptIdx] ?? "";
		let timeout: ReturnType<typeof setTimeout>;

		if (!isDeleting && displayed.length < target.length) {
			timeout = setTimeout(() => setDisplayed(target.slice(0, displayed.length + 1)), 45);
		} else if (!isDeleting && displayed.length === target.length) {
			timeout = setTimeout(() => setIsDeleting(true), 2200);
		} else if (isDeleting && displayed.length > 0) {
			timeout = setTimeout(() => setDisplayed(displayed.slice(0, -1)), 22);
		} else {
			setIsDeleting(false);
			setPromptIdx((i) => (i + 1) % prompts.length);
		}
		return () => clearTimeout(timeout);
	}, [displayed, isDeleting, promptIdx, prompts]);

	// Pressing anywhere on the box opens the auth modal, and the field cannot be typed in.
	// Nothing was ever wired to a real search, so a visitor could type a whole itinerary
	// prompt, press the button, and watch it vanish behind a signup modal — the animated
	// placeholder invites exactly that. Read-only keeps the animation as the invitation it is.
	//
	// The textarea is decorative now, so it leaves the tab order and the accessibility tree:
	// the button is the real control, and a keyboard or screen-reader user should land on it
	// rather than on a text field that ignores them.
	return (
		<div className="hero-search-card__plan-box" onClick={onSubmit}>
			<textarea
				className="hero-search-card__textarea"
				value=""
				readOnly
				tabIndex={-1}
				aria-hidden="true"
				placeholder={displayed || " "}
				rows={2}
			/>
			<div className="hero-search-card__plan-actions">
				<button
					type="button"
					className="hero-search-card__cta"
					onClick={(e) => {
						// The wrapper handles the click too; without this the tracking fires twice.
						e.stopPropagation();
						onSubmit?.();
					}}
				>
					{ctaLabel}
				</button>
			</div>
		</div>
	);
}

function PlanTabContent({ translations }: { translations: Record<string, string> }) {
	const planPrompts = useMemo(() => PLAN_PROMPT_KEYS.map(({ key, fallback }) => t(translations, key, fallback)), [translations]);

	// The gate lives in planGate.tsx and is shared with the /discover guide pages, which render
	// this same card. It used to live here, which is exactly how the two drifted apart — see
	// that file's header. `"store"` per 123yb691bdn: on mobile web every plan CTA goes to the
	// store, this one included.
	const gate = usePlanGate("home_plan_ai", "store");

	return (
		<>
			<AnimatedPlanBox
				prompts={planPrompts}
				ctaLabel={t(translations, "home.searchCard.planCta", "Plan with AI")}
				onSubmit={gate.requestPlan}
			/>
			{/* No `allowMobile`: a phone never reaches this modal from the plan box any more, so
			    the mobile-web signup path it existed to keep open has no caller left here. */}
			<PlanGateModal
				gate={gate}
				title={t(translations, "home.auth.planWithAi", "Create a free account to plan with AI")}
				loginTitle={t(translations, "home.auth.login", "Log in to plan with AI")}
			/>
		</>
	);
}

// Mirrors the search matching in ExplorePage's ExploreSearchInput
// (frontend/src/components/b2c/ExplorePage.tsx) — picking a suggestion (click, or
// Enter while highlighted) navigates straight to its country/destination page, same
// as ExploreSearchInput's handleSelect. The "Explore" button covers the case where
// the visitor typed a query and hit the CTA without picking a suggestion first.
type ExploreSearchResult =
	| { kind: "country"; slug: string; name: string; flag?: string }
	| { kind: "destination"; slug: string; name: string; countrySlug: string; countryName: string };

function ExploreTabContent({ translations }: { translations: Record<string, string> }) {
	const history = useHistory();
	const lang = useMarketingLang();
	const [query, setQuery] = useState("");
	const [debouncedQuery, setDebouncedQuery] = useState("");
	const [open, setOpen] = useState(false);
	const [activeIndex, setActiveIndex] = useState(-1);
	const [selected, setSelected] = useState<ExploreSearchResult | null>(null);
	const containerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const timeout = setTimeout(() => setDebouncedQuery(query.trim()), 250);
		return () => clearTimeout(timeout);
	}, [query]);

	const searchActive = debouncedQuery.length >= 2;
	// `withGuide` restricts results to geos whose page actually exists. Without it the search
	// offers all ~4,300 published Explore geos, and picking one of the ~4,100 with no guide
	// pushes /discover/<slug>, which matches no marketing route, falls through the SPA
	// catch-all and lands the visitor on the login screen. Same predicate loadReadyGeos() uses
	// for the sitemap and the noindex gate — a result you cannot open is not a result.
	const { data: countriesData } = useQuery(EXPLORE_COUNTRIES, { variables: { search: debouncedQuery, withGuide: true, limit: 5 }, skip: !searchActive });
	const { data: destinationsData } = useQuery(EXPLORE_DESTINATIONS, { variables: { search: debouncedQuery, withGuide: true, limit: 5 }, skip: !searchActive });

	const results = useMemo<ExploreSearchResult[]>(() => {
		if (!searchActive) return [];
		const countries: ExploreSearchResult[] = (countriesData?.exploreCountries || []).map((c: any) => ({
			kind: "country",
			slug: c.slug,
			name: c.name,
			flag: c.flag,
		}));
		const destinations: ExploreSearchResult[] = (destinationsData?.exploreDestinations || []).map((d: any) => ({
			kind: "destination",
			slug: d.slug,
			name: d.name,
			countrySlug: d.countrySlug,
			countryName: d.country,
		}));
		return [...countries, ...destinations].slice(0, 8);
	}, [searchActive, countriesData, destinationsData]);

	useEffect(() => {
		setActiveIndex(-1);
	}, [results]);

	useEffect(() => {
		const handleOutsideClick = (e: MouseEvent) => {
			if (containerRef.current?.contains(e.target as Node)) return;
			setOpen(false);
		};
		document.addEventListener("mousedown", handleOutsideClick);
		return () => document.removeEventListener("mousedown", handleOutsideClick);
	}, []);

	const goToResult = (item: ExploreSearchResult) => {
		trackEvent("search_discover", { query, result_type: item.kind, result_name: item.name }, "marketing_site");
		if (item.kind === "country") {
			history.push(discoverPath(`/discover/${item.slug}`, lang));
		} else {
			history.push(discoverPath(`/discover/${item.countrySlug}/${item.slug}`, lang));
		}
	};

	const selectResult = (item: ExploreSearchResult) => {
		setSelected(item);
		setQuery(item.name);
		setOpen(false);
		setActiveIndex(-1);
		goToResult(item);
	};

	const handleExplore = () => {
		const target = selected || results[0];
		if (!target) return;
		goToResult(target);
	};

	return (
		<div className="hero-search-card__panel">
			<div className="hero-search-card__body" ref={containerRef}>
				<div className="hero-search-card__input">
					<Icon name="search" size={18} fill="#13c892" />
					<input
						value={query}
						onChange={(e) => {
							setQuery(e.target.value);
							setSelected(null);
							setOpen(true);
						}}
						onFocus={() => setOpen(true)}
						onKeyDown={(e) => {
							if (!open || results.length === 0) return;
							if (e.key === "ArrowDown") {
								e.preventDefault();
								setActiveIndex((i) => (i + 1) % results.length);
							} else if (e.key === "ArrowUp") {
								e.preventDefault();
								setActiveIndex((i) => (i <= 0 ? results.length - 1 : i - 1));
							} else if (e.key === "Enter") {
								if (activeIndex >= 0) {
									e.preventDefault();
									selectResult(results[activeIndex]);
								}
							} else if (e.key === "Escape") {
								setOpen(false);
								setActiveIndex(-1);
							}
						}}
						role="combobox"
						aria-expanded={open && results.length > 0}
						aria-controls="hero-search-card-suggestions"
						placeholder={t(translations, "home.searchCard.explorePlaceholder", "Search a country or city...")}
					/>
					{open && results.length > 0 && (
						<div className="hero-search-card__suggestions" id="hero-search-card-suggestions" role="listbox">
							{results.map((item, index) => {
								const key = item.kind === "country" ? `country-${item.slug}` : `destination-${item.countrySlug}-${item.slug}`;
								return (
									<button
										key={key}
										type="button"
										className={`hero-search-card__suggestion ${index === activeIndex ? "active" : ""}`}
										role="option"
										aria-selected={index === activeIndex}
										onMouseEnter={() => setActiveIndex(index)}
										onClick={() => selectResult(item)}
									>
										{item.kind === "country" ? (
											<>
												{item.flag && <span className="hero-search-card__suggestion-flag">{item.flag}</span>}
												<span>{item.name}</span>
											</>
										) : (
											<>
												<span>{item.name}</span>
												<span className="hero-search-card__suggestion-country">{item.countryName}</span>
											</>
										)}
									</button>
								);
							})}
						</div>
					)}
				</div>
				<button type="button" className="hero-search-card__cta" onClick={handleExplore}>
					{t(translations, "home.searchCard.exploreCta", "Explore")}
				</button>
			</div>
			<div className="hero-search-card__trending">
				<p>{t(translations, "home.searchCard.trendingLabel", "Trending destinations")}</p>
				<div className="hero-search-card__trending-chips">
					{POPULAR_DESTINATIONS.map(({ label, geo, flag }) => (
						<a key={geo} href={discoverPath(`/discover/${geo}`, lang)}>
							<img src={`https://flagcdn.com/w40/${flag}.png`} alt="" loading="lazy" />
							{localizedDestinationLabel({ label, geo }, lang)}
						</a>
					))}
				</div>
			</div>
		</div>
	);
}

type FullVariantProps = {
	variant?: "full";
	onTabChange?: (tab: HeroSearchTab) => void;
};

type PlanVariantProps = {
	variant: "plan";
	/** Copy shown next to the "Plan" badge, e.g. "your Thailand trip with AI" */
	subtitle: string;
	/** Animated placeholder prompts specific to this country/destination */
	planPrompts: string[];
	/**
	 * Called when the visitor taps "Plan with AI". On /discover this is the page's own handler,
	 * which routes a logged-out visitor through the SAME `usePlanGate` the homepage uses — the
	 * page owns the gate because the identical click also arrives from six other section CTAs.
	 * See planGate.tsx for why this prop is not allowed to grow a second answer again.
	 */
	onSubmit: () => void;
};

type Props = FullVariantProps | PlanVariantProps;

// Tabbed "Plan" / "Explore" card — the homepage variant.
function FullHeroSearchCard({ onTabChange }: FullVariantProps) {
	const [tab, setTab] = useState<HeroSearchTab>("plan");
	const lang = useMarketingLang();
	const translations = getHomeTranslations(lang);

	const tabs: { id: HeroSearchTab; label: string; renderIcon: (size: number) => React.ReactNode }[] = [
		{ id: "plan", label: t(translations, "home.searchCard.tabPlan", "Plan"), renderIcon: (size) => <Icon name="sparkle--chunky" size={size} /> },
		{ id: "explore", label: t(translations, "home.searchCard.tabExplore", "Explore"), renderIcon: (size) => <Compass size={size} strokeWidth={2} /> },
	];

	const handleTabChange = (next: HeroSearchTab) => {
		setTab(next);
		onTabChange?.(next);
	};

	return (
		<div className="hero-search-card">
			<div className="hero-search-card__tabs">
				{tabs.map(({ id, label, renderIcon }) => (
					<button key={id} type="button" className={`hero-search-card__tab ${tab === id ? "active" : ""}`} onClick={() => handleTabChange(id)}>
						{renderIcon(TAB_ICON_SIZE)}
						<span className="hero-search-card__tab-label">{label}</span>
					</button>
				))}
			</div>
			{tab === "plan" ? <PlanTabContent translations={translations} /> : <ExploreTabContent translations={translations} />}
		</div>
	);
}

// Single-purpose "Plan" card, no Explore tab — used on country/destination guide
// pages where the whole page is already scoped to one place (see the redesign's
// CountryPage.tsx / CityPage.tsx Hero).
function PlanHeroSearchCard({ subtitle, planPrompts, onSubmit }: PlanVariantProps) {
	const lang = useMarketingLang();
	const translations = getHomeTranslations(lang);

	return (
		<div className="hero-search-card">
			<div className="hero-search-card__header">
				<span className="hero-search-card__badge">
					<Icon name="sparkle--chunky" size={TAB_ICON_SIZE} />
					<span className="hero-search-card__badge-label">{t(translations, "home.searchCard.tabPlan", "Plan")}</span>
				</span>
				<span className="hero-search-card__header-subtitle">{subtitle}</span>
			</div>
			<AnimatedPlanBox prompts={planPrompts} ctaLabel={t(translations, "home.searchCard.planCta", "Plan with AI")} onSubmit={onSubmit} />
		</div>
	);
}

/**
 * Decorative search widget matching the redesign's Hero — tab switching (and,
 * for the "plan" variant, submitting) is local UI state, inputs are not wired
 * to a real search yet.
 */
const HeroSearchCard = (props: Props) => {
	if (props.variant === "plan") return <PlanHeroSearchCard {...props} />;
	return <FullHeroSearchCard {...props} />;
};

export default HeroSearchCard;
